import React, { type JSX } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { Box, Link, Paragraph, Text } from '@rover/kibble/core';
import { ConfigureFieldInterface, ZodSchemaOrEffects } from '@rover/kibble/forms';
import CheckboxField from '@rover/kibble/forms/fields/CheckboxField';
import { CheckboxFieldOptionConfig } from '@rover/kibble/forms/fields/CheckboxField/CheckboxField.common';

type Props<Z extends ZodSchemaOrEffects> = {
  countryName?: string;
  countryCheckboxesField: ConfigureFieldInterface<Z>;
};

export default function CountryChangeAlert<Z extends ZodSchemaOrEffects>({
  countryName = '',
  countryCheckboxesField,
}: Props<Z>): JSX.Element {
  const options = [
    {
      primaryLabel: (
        <Trans>
          Yes, I agree to the Rover{' '}
          <Link
            asInlineLink
            href="https://www.rover.com/terms/tos/"
            target="_blank"
            onPress={(e?) => e?.webEvent?.stopPropagation()}
          >
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link
            asInlineLink
            href="https://www.rover.com/terms/privacy/"
            target="_blank"
            onPress={(e?) => e?.webEvent?.stopPropagation()}
          >
            Privacy Statement
          </Link>{' '}
          and confirm that I am 18 years of age or older.
        </Trans>
      ),
      value: 'agreeTOS',
    },
    {
      primaryLabel: t`Yes, I want to receive marketing emails and messages from Rover and its affiliates. I can unsubscribe at any time.`,
      value: 'emailOptIn',
    },
  ] as CheckboxFieldOptionConfig[];

  return (
    <Box mt="4x" background="highlight" p="4x" borderRadius="secondary" pl="6x">
      <Text size="200" fontWeight="semibold" mb="1x">
        {t`Moving to ${countryName}.`}
      </Text>
      <Paragraph size="200" mb="0x">
        {t`In order to use Rover in ${countryName}, you need to agree to updated terms & conditions.`}
      </Paragraph>
      <Box pr="6x">
        <CheckboxField options={options} {...countryCheckboxesField} primaryLabel="" />
      </Box>
    </Box>
  );
}
